import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field, useToast } from '@/components/ui';
import { usePatientActions } from '@/features/patients/api';
import {
  UNLINK_REASON_LIMITS,
  type AppAccount,
  type PatientDetail,
} from '@/features/patients/types';
import { isApiError } from '@/lib/api';
import {
  appAccountClearedText,
  appAccountTime,
  appLinkMethodLabel,
} from './app-account-format';
import styles from './PatientAppAccount.module.css';

const noop = () => {};
const { min: MIN, max: MAX } = UNLINK_REASON_LIMITS;

/**
 * Codes after which the record on screen is out of date: the dialog closes,
 * the record is reloaded and a toast says why.
 */
const RELOAD: Record<string, { title: string; body: string }> = {
  APP_ACCOUNT_NOT_LINKED: {
    title: 'No app account is linked any more',
    body: 'It was unlinked already, perhaps by someone else. The record has been reloaded.',
  },
  VERSION_CONFLICT: {
    title: 'Someone else changed this record',
    body: 'It has been reloaded. Check the app account and unlink again if it is still needed.',
  },
  PATIENT_MERGED: {
    title: 'This record was merged into another',
    body: 'Use the kept record. This record has been reloaded.',
  },
};

interface Failure {
  message: string;
  requestId?: string;
}

/**
 * RL-1185: staff unlink the app account from a record, verified or not
 * (POST /patients/:id/app-account/unlink { reason, version }, API RL-1184).
 * The effects are the same as counter eKYC "not theirs" (RL-1118), so the
 * wording follows AppAccountConfirm.
 */
export function UnlinkAppAccountDialog({
  patient,
  account,
  onClose,
}: {
  patient: Pick<PatientDetail, 'id' | 'version' | 'ekyc'>;
  account: AppAccount;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const formId = useId();
  const qc = useQueryClient();
  const toast = useToast();
  const { unlinkAppAccount } = usePatientActions();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string>();
  const [failure, setFailure] = useState<Failure | null>(null);
  const pending = unlinkAppAccount.isPending;
  const text = reason.trim();
  const verified = patient.ekyc.status === 'verified';

  const reload = () => {
    void qc.invalidateQueries({ queryKey: ['patient', patient.id] });
    void qc.invalidateQueries({ queryKey: ['patient-record', patient.id] });
    void qc.invalidateQueries({ queryKey: ['patients'] });
  };

  async function submit() {
    setFailure(null);
    if (text.length < MIN)
      return setReasonError(
        `Enter why the app account is unlinked (at least ${MIN} characters).`,
      );
    try {
      await unlinkAppAccount.mutateAsync({
        id: patient.id,
        reason: text,
        version: patient.version,
      });
      toast.show({
        tone: 'success',
        title: 'App account unlinked',
        body: 'It was signed out and can no longer see this record.',
      });
      onClose();
    } catch (err) {
      if (!isApiError(err))
        return setFailure({
          message:
            'Could not unlink the app account. Check the connection and try again.',
        });
      const field = err.fieldError('reason');
      if (field) return setReasonError(field);
      const known = RELOAD[err.code];
      if (known) {
        reload();
        toast.show({
          tone: 'error',
          title: known.title,
          body: err.requestId
            ? `${known.body} ${t('common.requestId', { id: err.requestId })}`
            : known.body,
        });
        onClose();
        return;
      }
      setFailure({
        message:
          err.code === 'PERMISSION_DENIED'
            ? // RL-1667 (gap #136): plain words, no permission code.
              "Your role can't unlink app accounts. Ask the clinic manager."
            : err.code === 'BRANCH_NOT_PERMITTED'
              ? `${err.message} Switch to a branch you work at and try again.`
              : err.message,
        requestId: err.requestId,
      });
    }
  }

  return (
    <Dialog
      open
      onClose={pending ? noop : onClose}
      variant="modal"
      dismissible={false}
      title="Unlink app account"
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="danger"
            loading={pending}
          >
            Unlink app account
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className={styles.dialog}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          // Rendered through a portal: keep the patient drawer's form (a
          // React ancestor) from saving too.
          e.stopPropagation();
          if (!pending) void submit();
        }}
      >
        <p className={styles.summary}>
          App account phone{' '}
          <span className="mono">{account.phone ?? 'not shown'}</span> ·{' '}
          {appLinkMethodLabel(account.method)} · linked{' '}
          {appAccountTime(account.linkedAt)}
        </p>
        <div
          className={styles.effects}
          role="note"
          aria-label="What unlinking does"
        >
          <strong>When you unlink this app account:</strong>
          <ul>
            <li>
              The app account is unlinked from this record and signed out of the
              app; whoever holds that phone can no longer see this record.
            </li>
            <li>
              Share links and codes that app account made for this record stop
              working.
            </li>
            <li>{appAccountClearedText(account.method)}</li>
            <li>
              {verified
                ? 'The record itself stays, and so does its eKYC verification.'
                : 'The record itself stays (still eKYC-unverified).'}{' '}
              Nothing is sent to the app. The change is audited.
            </li>
          </ul>
        </div>
        {failure && (
          <p className={styles.error} role="alert">
            {failure.message}
            {failure.requestId && (
              <span>{t('common.requestId', { id: failure.requestId })}</span>
            )}
          </p>
        )}
        <Field
          label="Reason"
          hint={`Kept for staff, never shown in the app. ${MIN}–${MAX} characters.`}
          error={reasonError}
        >
          <textarea
            className={styles.textarea}
            rows={3}
            maxLength={MAX}
            required
            value={reason}
            disabled={pending}
            onChange={(e) => {
              setReason(e.target.value);
              setReasonError(undefined);
            }}
          />
        </Field>
        <span className={styles.counter}>
          {text.length} / {MAX}
        </span>
      </form>
    </Dialog>
  );
}
